import {$pendingParams, definedSocialClickIds, paramCookies} from '@commercial-website/analytics'
import type {UrlCode} from '@commercial-website/data-layer/locale'
import {useStore} from '@nanostores/react'
// workaround for the Gatsby/Storybook bug - https://github.com/storybookjs/storybook/issues/17176
import {Link as GatsbyLink} from 'gatsby'
import React from 'react'

import {ctaClicked, elementClicked, ElementType} from '@/analytics/segment'
import {socialClickIdCookies} from '@/config/cookies'
import type {StateProps} from '@/hooks/use-navigate'
import {usePageSplitExperimentPaths} from '@/hooks/use-page-split-experiment-paths'
import {useLocale} from '@/providers/locale'
import {isMigratedRoute} from '@/utils/is-migrated-route'
import {removeTrailingSlash} from '@/utils/remove-trailing-slash'

type AnchorProps = JSX.IntrinsicElements['a'] & {
    referrerPolicy?: ReferrerPolicy
}

type LocalizedLinkProps = Omit<AnchorProps, 'href' | 'css'> & {
    to: string
    rawPath?: boolean
    locale?: UrlCode
    state?: StateProps
}

const ExperimentAnchor = React.forwardRef<
    HTMLAnchorElement,
    JSX.IntrinsicElements['a'] & {destinationPath: string}
>(({destinationPath, children, ...props}, ref) => {
    const pendingParams = useStore($pendingParams)
    return (
        <a href={buildHrefWithPendingParams(destinationPath, pendingParams)} {...props} ref={ref}>
            {children}
        </a>
    )
})

export function buildHrefWithPendingParams(
    destinationPath: string,
    pendingParams: URLSearchParams | null,
): string {
    if (!pendingParams) return destinationPath

    const [pathAndQuery, hash] = destinationPath.split('#') as [string, string | undefined]
    const [path, existingSearch] = pathAndQuery.split('?') as [string, string | undefined]
    const search = new URLSearchParams(existingSearch)

    for (const {paramName} of paramCookies) {
        const value = pendingParams.get(paramName)
        if (value !== null) {
            search.set(paramName, value)
        }
    }

    const searchString = search.toString()
    const base = searchString ? `${path}?${searchString}` : path
    return hash !== undefined ? `${base}#${hash}` : base
}

export const LocalizedLink = React.forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
    ({children, to, locale, rawPath, tabIndex = 0, ...props}, ref) => {
        const currentLocale = useLocale()
        const urlLocaleCode = locale ?? currentLocale.urlCode
        const destinationPath = rawPath ? to : `/${urlLocaleCode}${to}`

        // Get all paths (regex patterns) that have experiments on
        const experimentPaths = usePageSplitExperimentPaths()
        // Check if the destination path has an experiment running on it
        const destinationPathHasExperiment = experimentPaths?.some((experimentPath) =>
            new RegExp(experimentPath).test(destinationPath),
        )

        // If the destination path has an experiment running on it, we don't
        // want to use a GatsbyLink since it uses JS navigation and the request
        // doesn't go through the Lambdas. We need to use a regular anchor tag
        // for pages with experiments to cause a page refresh which sends the
        // request through to the Lambdas which can then run the experiment
        // logic to determine where the user should end up.
        //
        // The same applies to routes migrated to Astro: client-side navigation
        // would render Gatsby's stale legacy page from page-data.json instead of
        // letting the origin-request Lambda rewrite the request to the Astro
        // build in the unified bucket. A hard navigation ensures the Lambda runs.
        const bypassClientSideRouting =
            destinationPathHasExperiment || isMigratedRoute(destinationPath)

        if (process.env.FRAMEWORK === 'gatsby' && !bypassClientSideRouting) {
            return (
                <GatsbyLink
                    ref={ref as any}
                    {...props}
                    to={removeTrailingSlash(destinationPath)}
                    tabIndex={tabIndex}
                >
                    {children}
                </GatsbyLink>
            )
        }

        return (
            <ExperimentAnchor
                ref={ref}
                destinationPath={destinationPath}
                {...props}
                tabIndex={tabIndex}
            >
                {children}
            </ExperimentAnchor>
        )
    },
)

interface TrackedLocalizedLinkProps extends LocalizedLinkProps {
    trackingData: {
        linkText: string
        linkType: 'cta' | 'element'
        elementLocation: string
    }
}

export const TrackedLocalizedLink = React.forwardRef<HTMLAnchorElement, TrackedLocalizedLinkProps>(
    ({trackingData, to, onClick, ...rest}, ref) => {
        const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
            const trackingPayload = {
                elementLocation: trackingData.elementLocation,
                elementType: ElementType.Link,
                elementText: trackingData.linkText,
                destinationUrl: to,
            }

            if (trackingData.linkType === 'cta') {
                ctaClicked({
                    ...trackingPayload,
                    ...definedSocialClickIds(socialClickIdCookies),
                })
            } else {
                elementClicked(trackingPayload)
            }

            onClick?.(e)
        }

        return <LocalizedLink ref={ref} to={to} onClick={handleClick} {...rest} />
    },
)
